import React, { useState, useEffect } from 'react';
import { BACKEND_URL, DEFAULT_QR_CODE_URL } from '../config';
import { QRCodeSVG } from 'qrcode.react';
import { useSystemFeatures } from '../hooks/useSystemFeatures';
import { splitTaxInclusive, taxLabel } from '../utils/systemFeatures';
import { receiptPaymentLabel, tenderTipTotal } from '../utils/paymentMethods';

const PrintTicket = React.forwardRef(({ items = [], tab, table, orderId, subtotal, discount, total, closedAt, payments = [] }, ref) => {
  const { taxRate } = useSystemFeatures();
  const [settings, setSettings] = useState(null);
  const [orderWithSpecial, setOrderWithSpecial] = useState(null);

  // Load printer settings on mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        // Try to fetch from API first
        const response = await fetch(`${BACKEND_URL}/api/printer-settings`);
        if (response.ok) {
          const data = await response.json();
          setSettings(data);
        } else {
          throw new Error('API failed');
        }
      } catch (err) {
        console.warn('Error fetching settings from API, using localStorage fallback:', err);
        // Fallback to localStorage
        const receiptFormat = JSON.parse(localStorage.getItem('receiptFormat') || '{}');
        setSettings({
          businessName: receiptFormat.businessName || 'CERVECERIA GIBBONS',
          address: receiptFormat.address || 'C. ESCORZA 848',
          taxId: receiptFormat.taxId || 'CGI181112777',
          footerMessage1: receiptFormat.footerMessage1 || '',
          footerMessage2: receiptFormat.footerMessage2 || '',
          footerMessage3: receiptFormat.footerMessage3 || '',
          showQrCode: receiptFormat.showQrCode || false,
          qrCodeUrl: receiptFormat.qrCodeUrl || DEFAULT_QR_CODE_URL,
          qrCodeText: receiptFormat.qrCodeText || 'Escanea para visitar:',
          includeIva: receiptFormat.includeIva !== undefined ? receiptFormat.includeIva : true
        });
      }
    };
    loadSettings();
  }, []);

  // Fetch order data to get special name
  useEffect(() => {
    const fetchOrderData = async () => {
      if (!orderId) return;

      try {
        const response = await fetch(`${BACKEND_URL}/api/orders/${orderId}`);
        if (response.ok) {
          const data = await response.json();
          setOrderWithSpecial(data);
        }
      } catch (err) {
        console.error('Error fetching order data:', err);
      }
    };

    fetchOrderData();
  }, [orderId]);

  // Calculate subtotal, discount, and total if not provided
  const computedSubtotal = subtotal !== undefined
    ? Number(subtotal)
    : items.reduce((sum, item) => sum + (Number(item.price || 0) * item.quantity), 0);
  const computedDiscount = discount !== undefined
    ? Number(discount)
    : items.reduce((sum, item) => sum + (Number(item.discount || 0) * item.quantity), 0);
  const computedTotal = total !== undefined
    ? Number(total)
    : (computedSubtotal - computedDiscount);

  // Use closedAt for closed orders, current time for active
  const now = closedAt ? new Date(closedAt) : new Date();
  const dateTime = now.toLocaleString('es-MX', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });

  // Show loading or use defaults if settings not loaded yet
  if (!settings) {
    return (
      <div ref={ref} style={{ padding: 8 }}>
        Loading printer settings...
      </div>
    );
  }

    return (
      <>
        <style>{`
          @media print {
            html, body { margin: 0 !important; padding: 0 !important; background: #fff !important; }
            .ticket-print-area { margin: 0 !important; padding: 0 !important; box-shadow: none !important; border: none !important; }
            .ticket-print-area div { display: block !important; }
            .ticket-print-area span { display: inline-block !important; }
            * { -webkit-print-color-adjust: exact !important; color-adjust: exact !important; }
          }
        `}</style>
        <div ref={ref} className="ticket-print-area" style={{ background: '#fff', color: '#000', fontFamily: '"Courier New", "Lucida Console", "Monaco", monospace', padding: 8, borderRadius: 0, maxWidth: '320px' }}>
          {/* Header - centered and bold */}
          <div style={{ textAlign: 'center', fontWeight: 'bold', marginBottom: 4 }}>{settings.businessName}</div>
          <div style={{ textAlign: 'center', marginBottom: 4 }}>{settings.address}</div>
          <div style={{ textAlign: 'center', marginBottom: 8 }}>{settings.taxId}</div>
          <div style={{ borderBottom: '1px solid #000', marginBottom: 8 }}></div>

          {/* Order info */}
          <div style={{ marginBottom: 4 }}>Fecha/Hora: {dateTime}</div>
          <div style={{ marginBottom: 4 }}>Orden #: {orderId}</div>
          <div style={{ marginBottom: 4 }}>Mesa: {table?.name || 'N/A'}</div>
          {tab && tab.name && table?.name !== tab.name && (
            <div style={{ marginBottom: 8 }}>Nombre: {tab.name}</div>
          )}

          {/* Special/promotion section */}
          {orderWithSpecial?.special_name && (
            <>
              <div style={{ textAlign: 'center', marginTop: 8, marginBottom: 4, fontWeight: 'bold' }}>
                --- PROMOCION APLICADA ---
              </div>
              <div style={{ textAlign: 'center', marginBottom: 4, fontWeight: 'bold' }}>
                {orderWithSpecial.special_name}
              </div>
            </>
          )}

          {/* Items header */}
          <div style={{ borderTop: '1px dashed #000', borderBottom: '1px dashed #000', padding: '4px 0', marginBottom: 4 }}>
            CANT PRODUCTO             PRECIO
          </div>

          {/* Items */}
          {(() => {
            // eslint-disable-next-line no-unused-vars
            let totalDiscount = 0;
            return items.map(item => {
              const qty = item.quantity;
              const name = item.product_name || item.name || '';
              const price = Number(item.price || 0);
              const discount = Number(item.discount || 0);
              const itemTotal = qty * (price - discount);
              const lineDiscount = discount * qty;
              totalDiscount += lineDiscount;

              return (
                <div key={item.id} style={{ marginBottom: 2 }}>
                  <div style={{ fontSize: '0.9rem' }}>
                    {String(qty).padStart(2, ' ')}x {name} ${itemTotal.toFixed(2)}
                  </div>
                  {discount > 0 && (
                    <div style={{ fontSize: '0.8rem', paddingLeft: '20px' }}>
                      (Descuento: -${lineDiscount.toFixed(2)})
                    </div>
                  )}
                </div>
              );
            });
          })()}

          {/* Totals */}
          <div style={{ borderTop: '1px dashed #000', marginTop: 8, paddingTop: 4 }}>
            {(() => {
              // Calculate total discount
              const totalDiscount = items.reduce((sum, item) => {
                return sum + (Number(item.discount || 0) * item.quantity);
              }, 0);

              return (
                <>
                  {totalDiscount > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontWeight: 'bold' }}>
                      <span>Descuento:</span>
                      <span>-${totalDiscount.toFixed(2)}</span>
                    </div>
                  )}
                  {settings.includeIva && (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                        <span>Subtotal:</span>
                        <span>${splitTaxInclusive(computedTotal, taxRate).subtotal.toFixed(2)}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                        <span>{taxLabel(taxRate)}:</span>
                        <span>${splitTaxInclusive(computedTotal, taxRate).tax.toFixed(2)}</span>
                      </div>
                    </>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', marginBottom: 4 }}>
                    <span>TOTAL:</span>
                    <span>${computedTotal.toFixed(2)}</span>
                  </div>
                </>
              );
            })()}
          </div>

          {/* How the check was paid, one line per tender */}
          {payments.length > 0 && (
            <div style={{ borderTop: '1px dashed #000', marginTop: 4, paddingTop: 4 }}>
              <div style={{ marginBottom: 2 }}>FORMA DE PAGO</div>
              {payments.map(payment => (
                <div key={payment.id} style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>{receiptPaymentLabel(payment.payment_type_id)}</span>
                  <span>${Number(payment.amount).toFixed(2)}</span>
                </div>
              ))}
              {tenderTipTotal(payments) > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Propina:</span>
                  <span>${tenderTipTotal(payments).toFixed(2)}</span>
                </div>
              )}
            </div>
          )}

          <div style={{ borderBottom: '1px solid #000', marginBottom: 8 }}></div>

          {/* Footer - centered */}
          {(settings.footerMessage1 || settings.footerMessage2 || settings.footerMessage3) && (
            <div style={{ textAlign: 'center', marginTop: 8, fontSize: '0.9rem' }}>
              {settings.footerMessage1 && <>{settings.footerMessage1}<br /></>}
              {settings.footerMessage2 && <>{settings.footerMessage2}<br /></>}
              {settings.footerMessage3 && (
                <>
                  <br />
                  {settings.footerMessage3}
                </>
              )}
            </div>
          )}

          {/* QR Code - centered */}
          {settings.showQrCode && settings.qrCodeUrl && (
            <div style={{ textAlign: 'center', marginTop: 16, marginBottom: 8 }}>
              <QRCodeSVG
                value={settings.qrCodeUrl}
                size={128}
                level="L"
                includeMargin={false}
              />
              <div style={{ fontSize: '0.85rem', marginTop: 8 }}>
                {settings.qrCodeText}
              </div>
            </div>
          )}

          <div style={{ borderBottom: '1px solid #000', marginTop: 8 }}></div>

          <div style={{ height: '40px' }}>&nbsp;</div>
        </div>
      </>
    );
});

export default PrintTicket;